import { useCallback, useEffect, useRef, useState } from 'react';
import { ALLOWED_ATTACHMENT_TYPES, MAX_ATTACHMENTS_PER_REPORT, MAX_ATTACHMENT_BYTES } from '../../constants/feedback';

export interface PickedAttachment {
  /** Stable key for React — File has no id and two pastes can share a name. */
  key: string;
  file: File;
  previewUrl: string;
}

/**
 * Collect screenshots by paste, drop or file picker.
 *
 * Paste is the primary path deliberately. Capturing the DOM instead
 * (html2canvas and friends) renders the Plotter's canvas/WebGL charts
 * unreliably — which is exactly the content people file reports about —
 * so the user takes a real screenshot with their OS and pastes it. Less
 * magical, always correct.
 *
 * The window-level paste listener lives for exactly the hook's lifetime.
 * The panel mounts only while open, so Ctrl+V elsewhere in the app is
 * never intercepted.
 */
export function useAttachmentPicker() {
  const [attachments, setAttachments] = useState<PickedAttachment[]>([]);
  const [rejection, setRejection] = useState<string | null>(null);

  // Mirrors `attachments` for the two places that must not close over a
  // stale render: the cap check below and the unmount revoke at the bottom.
  const attachmentsRef = useRef<PickedAttachment[]>([]);
  useEffect(() => {
    attachmentsRef.current = attachments;
  }, [attachments]);

  // Object URLs are minted here rather than inside the setAttachments
  // updater: an updater must be pure, and StrictMode double-invokes it,
  // which would mint two blob URLs per file and only ever revoke one.
  const addFiles = useCallback((incoming: File[]) => {
    const accepted: PickedAttachment[] = [];
    const problems: string[] = [];

    incoming.forEach((file) => {
      if (!ALLOWED_ATTACHMENT_TYPES.includes(file.type)) {
        problems.push(`${file.name || 'image'}: only PNG, JPEG and WebP are accepted`);
      } else if (file.size > MAX_ATTACHMENT_BYTES) {
        problems.push(`${file.name || 'image'}: over ${Math.round(MAX_ATTACHMENT_BYTES / 1024 / 1024)} MB`);
      } else if (attachmentsRef.current.length + accepted.length >= MAX_ATTACHMENTS_PER_REPORT) {
        problems.push(`at most ${MAX_ATTACHMENTS_PER_REPORT} images per report`);
      } else {
        accepted.push({
          key: `${file.name}-${file.size}-${Date.now()}-${accepted.length}`,
          file,
          previewUrl: URL.createObjectURL(file),
        });
      }
    });

    setRejection(problems.length ? problems[0] : null);
    if (accepted.length) {
      attachmentsRef.current = [...attachmentsRef.current, ...accepted];
      setAttachments((current) => [...current, ...accepted]);
    }
  }, []);

  const remove = useCallback((key: string) => {
    setAttachments((current) => {
      const target = current.find((item) => item.key === key);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return current.filter((item) => item.key !== key);
    });
  }, []);

  const reset = useCallback(() => {
    setAttachments((current) => {
      current.forEach((item) => URL.revokeObjectURL(item.previewUrl));
      return [];
    });
    setRejection(null);
  }, []);

  useEffect(() => {
    const onPaste = (event: ClipboardEvent): void => {
      const files = Array.from(event.clipboardData?.files ?? []);
      if (files.length) {
        event.preventDefault();
        addFiles(files);
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [addFiles]);

  // Object URLs are a real leak if the panel closes with images attached.
  // Reads the ref, not `attachments` — a cleanup with [] deps would close
  // over the first render's empty array and revoke nothing.
  useEffect(() => () => attachmentsRef.current.forEach((item) => URL.revokeObjectURL(item.previewUrl)), []);

  return { attachments, addFiles, remove, reset, rejection };
}
